import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectUserProjectId } from 'core/userContext';
import { DataFeed } from 'modules/dataFeeds/types';
import { addNegativeToast } from 'modules/toastContainer';
import { ApiResponse, getRaw } from 'services/api';
import { FormField } from 'ui/formField';
import { Link } from 'ui/links';
import { MultiFilterSelect } from 'ui/selects';
import { spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { SMSContentDefinition } from 'modules/graphql';
import { CheckboxField, SettingsReadOnlyText, TwoRowFieldDisplay } from '.';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { SettingsFieldMoreDetailInRow } from './EditorContentContainer.styled';
import { formatDataFeedOptions, getDataFeedNumberDisplay, getIsEditMode } from './utils';

const DISPLAY_CONTENT = {
  checkboxLabel: 'Connect data feeds for dynamic template creation',
  labelText: 'Data feeds',
  additionalFields: {
    cacheDataFeeds: {
      formLabel: 'Cache data feed response (up to 1 hr)',
      checkboxLabel:
        'Reduces load on your server and speeds up the send due to faster template rendering',
    },
    mergeDataFeedAndUserContext: {
      formLabel: 'Merge the data feed and user contexts',
      checkboxLabel:
        'Allows accessing data feed variables through the usual curly braces instead of square brackets. Collisions will be resolved by preferring user fields over datafeed fields.',
    },
    useDataFeedAlias: {
      formLabel: 'Use data feed alias',
      checkboxLabel:
        'If an alias was provided on the data feed setup, the feed data will become available under that variable (rather than at top level).',
    },
  },
};

const DataFeedsFormSectionWrapper = styled.div`
  [data-test='multi-select-data-feeds-select'] {
    margin-top: ${spacing.half};
    margin-left: ${spacing.plusHalf};
    width: 95%;
  }
  [data-test='form-field'] {
    margin-top: ${spacing.plusHalf};
  }
`;

const LinkWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: right;
  margin-top: ${spacing.half};
`;

const PROJECT_DATA_FEEDS_URL = '/i/templates/dataFeeds.json'; // OK for RBAC: ManageBaseTemplates(1)

const getProjectDataFeeds = (projectId: number): Promise<ApiResponse> => {
  return getRaw(`${PROJECT_DATA_FEEDS_URL}?projectId=${projectId}`);
};

export const DataFeedsFormSection = ({ isEditable = true }: { isEditable?: boolean }) => {
  const dispatch = useDispatch();
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentTemplateField } = useEditableFieldsContext();
  const { dataFeedIds = [] } = liveTemplate;
  const [dataFeedsEnabled, setDataFeedsEnabled] = useState<boolean>(!!dataFeedIds.length);
  const [dataFeedOptions, setDataFeedOptions] = useState<{ [key: string]: DataFeed }>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const isEditModeVisible = getIsEditMode(view, isEditMode) && isEditable;
  const projectId = useSelector(selectUserProjectId);
  const isDataFeedIdsExist = dataFeedIds?.length > 0;
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const isContactCardEnabled = Number.isInteger(smsData.verifiedContactCardId);

  const handleCheckboxClick = () => {
    /** Clear data feed id's in live template when unchecking box */
    if (dataFeedsEnabled) {
      setCurrentTemplateField('dataFeedIds', []);
    }
    setDataFeedsEnabled(!dataFeedsEnabled);
  };

  const getDataFeeds = useCallback(() => {
    getProjectDataFeeds(projectId || 0)
      .then(({ data }: { data: { data: { [key: string]: DataFeed }; message: string } }) => {
        setDataFeedOptions(data.data || {});
      })
      .catch((err: any) => {
        dispatch(addNegativeToast('Unable to fetch data feed options', err));
      });
  }, [projectId]);

  useEffect(() => {
    getDataFeeds();
  }, []);

  /**
   * Reset data feeds enabled to false if no data feed was selected.
   * Upon Edit mode rendering, check checkbox if data exists, if not uncheck.
   * */
  useEffect(() => {
    if (!isEditModeVisible) {
      if (dataFeedsEnabled && !dataFeedIds?.length) {
        setDataFeedsEnabled(false);
      }
    } else if (isEditModeVisible) {
      if (isDataFeedIdsExist) {
        setDataFeedsEnabled(true);
      } else {
        setDataFeedsEnabled(false);
      }
    }
  }, [isEditModeVisible]);

  return (
    <DataFeedsFormSectionWrapper data-test="data-feeds-form-field">
      <FormField labelText={DISPLAY_CONTENT.labelText} isHorizontalLayout>
        {isEditModeVisible ? (
          <>
            <Tooltip
              isEnabled={isContactCardEnabled}
              tooltipContent={'Contact cards cannot use dynamic content from data feeds'}
            >
              <CheckboxField
                label={DISPLAY_CONTENT.checkboxLabel}
                status={dataFeedsEnabled}
                onClick={handleCheckboxClick}
              />
            </Tooltip>
            {dataFeedsEnabled && (
              <>
                <MultiFilterSelect
                  id="data-feeds-select"
                  groupBySelectedOptions
                  defaultValues={dataFeedIds.map((id) => id.toString())}
                  disableInternalFiltering
                  onSelect={(newSelections) =>
                    setCurrentTemplateField(
                      'dataFeedIds',
                      newSelections.map((id) => parseInt(id, 10)),
                    )
                  }
                  onSearchChange={(searchedItem) => setSearchTerm(searchedItem ?? '')}
                  options={formatDataFeedOptions(dataFeedOptions, searchTerm)}
                  optionWrapperWidth="100%"
                  placeholder="Select a data feed..."
                />
                <LinkWrapper>
                  <Link
                    hasOpenNewTabIcon
                    size="caption"
                    href="/integrations/dataFeeds"
                    target="_blank"
                  >
                    Add new data feed
                  </Link>
                </LinkWrapper>
              </>
            )}
          </>
        ) : (
          <TwoRowFieldDisplay>
            <SettingsReadOnlyText isUnset={!dataFeedsEnabled} data-test="data-feed-on-off">
              {isDataFeedIdsExist ? getDataFeedNumberDisplay(dataFeedIds) : 'None'}
            </SettingsReadOnlyText>
            <SettingsFieldMoreDetailInRow data-test="data-feed-detail">
              {isDataFeedIdsExist &&
                dataFeedIds.map((id) => dataFeedOptions[id.toString()]?.name).join(', ')}
            </SettingsFieldMoreDetailInRow>
          </TwoRowFieldDisplay>
        )}
      </FormField>
      {isEditModeVisible && dataFeedsEnabled && (
        <>
          <FormField
            isHorizontalLayout
            labelText={DISPLAY_CONTENT.additionalFields.cacheDataFeeds.formLabel}
          >
            <CheckboxField
              label={DISPLAY_CONTENT.additionalFields.cacheDataFeeds.checkboxLabel}
              onClick={() => setCurrentTemplateField('cacheDataFeed', !liveTemplate.cacheDataFeed)}
              status={liveTemplate?.cacheDataFeed}
            />
          </FormField>
          <FormField
            isHorizontalLayout
            labelText={DISPLAY_CONTENT.additionalFields.mergeDataFeedAndUserContext.formLabel}
          >
            <CheckboxField
              label={DISPLAY_CONTENT.additionalFields.mergeDataFeedAndUserContext.checkboxLabel}
              onClick={() =>
                setCurrentTemplateField('mergeDataFeedContext', !liveTemplate.mergeDataFeedContext)
              }
              status={liveTemplate?.mergeDataFeedContext}
            />
          </FormField>
          <FormField
            isHorizontalLayout
            labelText={DISPLAY_CONTENT.additionalFields.useDataFeedAlias.formLabel}
          >
            <CheckboxField
              label={DISPLAY_CONTENT.additionalFields.useDataFeedAlias.checkboxLabel}
              onClick={() =>
                setCurrentTemplateField(
                  'referenceDataFeedsByAlias',
                  !liveTemplate.referenceDataFeedsByAlias,
                )
              }
              status={liveTemplate?.referenceDataFeedsByAlias}
            />
          </FormField>
        </>
      )}
    </DataFeedsFormSectionWrapper>
  );
};
